import { CdkDragEnd, CdkDragMove, DragDropModule } from '@angular/cdk/drag-drop';
import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { finalize } from 'rxjs';
import { LocalizationService } from '../../core/localization/localization.service';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { DynamicApiService } from './dynamic-api.service';
import {
  DynamicMetadataEntityCatalog,
  DynamicMetadataFieldCatalog
} from './dynamic.models';

interface ErdNode {
  id: string;
  logicalKey: string;
  displayName: string;
  fields: DynamicMetadataFieldCatalog[];
}

interface ErdEdge {
  id: string;
  sourceId: string;
  targetId: string;
  type: string;
  label: string;
}

interface NodePosition {
  x: number;
  y: number;
}

const NODE_WIDTH = 240;
const NODE_GAP_X = 80;
const NODE_GAP_Y = 60;
const NODE_HEADER_HEIGHT = 44;
const NODE_FIELD_ROW_HEIGHT = 22;
const NODE_PADDING = 10;
const MAX_VISIBLE_FIELDS = 6;

@Component({
  standalone: true,
  selector: 'ain-dynamic-erd',
  imports: [AinStateComponent, InfoHintComponent, DragDropModule],
  template: `
    <section class="erd-view">
      <div class="section-header">
        <h3>{{ t('dynamic.relationship.erd') }}</h3>
        <ain-info-hint
          [titleKey]="'dynamic.erd.info.title'"
          [descriptionKey]="'dynamic.erd.info.description'"
          [bulletKeys]="[
            'dynamic.erd.info.bullets.scope',
            'dynamic.erd.info.bullets.drag'
          ]" />
      </div>

      @if (loading()) {
        <ain-state type="loading" [title]="t('common.loading')" />
      } @else if (error()) {
        <ain-state type="error" [title]="t('common.loadFailed')" [message]="error() ?? ''" />
      } @else if (!nodes().length) {
        <ain-state type="empty" [title]="t('dynamic.relationship.erd')" [message]="t('dynamic.erd.empty')" />
      } @else {
        <div class="canvas-wrap">
          <div class="canvas" [style.width.px]="canvasWidth()" [style.height.px]="canvasHeight()">
            <svg class="edges" [attr.width]="canvasWidth()" [attr.height]="canvasHeight()" aria-hidden="true">
              @for (edge of edgeGeometries(); track edge.id) {
                <g>
                  <path [attr.d]="edge.path" class="edge-line" />
                  <text [attr.x]="edge.labelX" [attr.y]="edge.labelY" class="edge-label">{{ edge.label }}</text>
                </g>
              }
            </svg>

            @for (node of nodes(); track node.id) {
              <article
                class="node card"
                cdkDrag
                [style.left.px]="positionFor(node.id).x"
                [style.top.px]="positionFor(node.id).y"
                [style.width.px]="nodeWidth"
                [attr.aria-label]="node.displayName"
                (cdkDragMoved)="onDragMoved(node.id, $event)"
                (cdkDragEnded)="onDragEnded(node.id, $event)">
                <header class="node-header">
                  <strong>{{ node.displayName }}</strong>
                  <span class="node-key">{{ node.logicalKey }}</span>
                </header>
                <ul class="node-fields">
                  @for (field of visibleFields(node); track field.logicalKey) {
                    <li>
                      <span class="field-name">{{ field.logicalKey }}</span>
                      <span class="field-type">{{ field.storageType }}</span>
                    </li>
                  }
                </ul>
              </article>
            }
          </div>
        </div>
      }
    </section>
  `,
  styles: [`
    .erd-view { display: grid; gap: 12px; }
    .section-header { display: grid; gap: 8px; }
    .section-header h3 { margin: 0; }
    .canvas-wrap { overflow: auto; border: 1px solid #e5e7eb; border-radius: 10px; background: #f8fafc; max-height: min(72vh, 900px); }
    .canvas { position: relative; min-width: 100%; min-height: 480px; }
    .edges { position: absolute; inset: 0; pointer-events: none; overflow: visible; }
    .edge-line { fill: none; stroke: #64748b; stroke-width: 2; }
    .edge-label { fill: #334155; font-size: 11px; text-anchor: middle; dominant-baseline: middle; }
    .node { position: absolute; border: 1px solid #cbd5e1; border-radius: 10px; background: #fff; box-shadow: 0 2px 8px rgba(15, 23, 42, 0.08); cursor: grab; z-index: 1; }
    .node:active { cursor: grabbing; }
    .node-header { display: grid; gap: 2px; padding: 10px 12px; border-bottom: 1px solid #e2e8f0; background: #f1f5f9; border-radius: 10px 10px 0 0; }
    .node-header strong { font-size: 0.95rem; color: #0f172a; }
    .node-key { font-size: 0.75rem; color: #64748b; }
    .node-fields { list-style: none; margin: 0; padding: 8px 12px 10px; display: grid; gap: 4px; }
    .node-fields li { display: flex; justify-content: space-between; gap: 8px; font-size: 0.8rem; color: #334155; }
    .field-type { color: #64748b; font-size: 0.75rem; }
  `]
})
export class DynamicErdComponent implements OnInit {
  private readonly api = inject(DynamicApiService);
  private readonly localization = inject(LocalizationService);

  readonly nodeWidth = NODE_WIDTH;

  readonly loading = signal(true);
  readonly error = signal<string | null>(null);
  readonly nodes = signal<ErdNode[]>([]);
  readonly edges = signal<ErdEdge[]>([]);
  readonly positions = signal<Record<string, NodePosition>>({});
  readonly activeDrag = signal<{ nodeId: string; dx: number; dy: number } | null>(null);
  readonly dragTick = signal(0);

  readonly canvasWidth = computed(() => {
    const positions = this.positions();
    const nodes = this.nodes();
    if (!nodes.length) {
      return 800;
    }
    const maxX = Math.max(...nodes.map(node => (positions[node.id]?.x ?? 0) + NODE_WIDTH));
    return Math.max(800, maxX + NODE_GAP_X);
  });

  readonly canvasHeight = computed(() => {
    const positions = this.positions();
    const nodes = this.nodes();
    if (!nodes.length) {
      return 480;
    }
    const maxY = Math.max(...nodes.map(node => (positions[node.id]?.y ?? 0) + this.nodeHeight(node)));
    return Math.max(480, maxY + NODE_GAP_Y);
  });

  readonly edgeGeometries = computed(() => {
    this.dragTick();
    return this.edges().map(edge => {
      const source = this.effectivePosition(edge.sourceId);
      const target = this.effectivePosition(edge.targetId);
      if (!source || !target) {
        return { id: edge.id, path: '', label: edge.label, labelX: 0, labelY: 0 };
      }

      const sourceNode = this.nodes().find(item => item.id === edge.sourceId);
      const targetNode = this.nodes().find(item => item.id === edge.targetId);
      const sourceHeight = sourceNode ? this.nodeHeight(sourceNode) : 120;
      const targetHeight = targetNode ? this.nodeHeight(targetNode) : 120;

      const x1 = source.x + NODE_WIDTH;
      const y1 = source.y + sourceHeight / 2;
      const x2 = target.x;
      const y2 = target.y + targetHeight / 2;
      const midX = (x1 + x2) / 2;

      return {
        id: edge.id,
        path: `M ${x1} ${y1} L ${midX} ${y1} L ${midX} ${y2} L ${x2} ${y2}`,
        label: edge.label,
        labelX: midX,
        labelY: (y1 + y2) / 2 - 6
      };
    });
  });

  ngOnInit(): void {
    this.loadCatalog();
  }

  positionFor(nodeId: string): NodePosition {
    return this.positions()[nodeId] ?? { x: 0, y: 0 };
  }

  visibleFields(node: ErdNode): DynamicMetadataFieldCatalog[] {
    return node.fields.slice(0, MAX_VISIBLE_FIELDS);
  }

  nodeHeight(node: ErdNode): number {
    const fieldCount = Math.min(node.fields.length, MAX_VISIBLE_FIELDS);
    return NODE_HEADER_HEIGHT + NODE_PADDING + fieldCount * NODE_FIELD_ROW_HEIGHT;
  }

  onDragMoved(nodeId: string, event: CdkDragMove): void {
    this.activeDrag.set({ nodeId, dx: event.distance.x, dy: event.distance.y });
    this.dragTick.update(value => value + 1);
  }

  onDragEnded(nodeId: string, event: CdkDragEnd): void {
    const current = this.positions()[nodeId] ?? { x: 0, y: 0 };
    const distance = event.distance;
    this.positions.update(map => ({
      ...map,
      [nodeId]: { x: current.x + distance.x, y: current.y + distance.y }
    }));
    this.activeDrag.set(null);
    event.source.reset();
    this.dragTick.update(value => value + 1);
  }

  private effectivePosition(nodeId: string): NodePosition {
    const base = this.positions()[nodeId] ?? { x: 0, y: 0 };
    const drag = this.activeDrag();
    if (drag?.nodeId === nodeId) {
      return { x: base.x + drag.dx, y: base.y + drag.dy };
    }
    return base;
  }

  private loadCatalog(): void {
    this.loading.set(true);
    this.error.set(null);

    this.api.getCatalog().pipe(
      finalize(() => this.loading.set(false))
    ).subscribe({
      next: catalog => {
        const entities = catalog.entities ?? [];
        this.nodes.set(entities.map(item => this.toNode(item)));
        this.edges.set(this.buildEdges(entities));
        this.positions.set(this.buildInitialLayout(entities));
      },
      error: err => {
        this.nodes.set([]);
        this.edges.set([]);
        this.error.set(err?.message ?? this.t('common.loadFailed'));
      }
    });
  }

  private toNode(entity: DynamicMetadataEntityCatalog): ErdNode {
    return {
      id: entity.id,
      logicalKey: entity.logicalKey,
      displayName: entity.displayName,
      fields: entity.fields ?? []
    };
  }

  private buildEdges(entities: DynamicMetadataEntityCatalog[]): ErdEdge[] {
    const keyToId = new Map(entities.map(item => [item.logicalKey, item.id]));
    const seen = new Set<string>();
    const edges: ErdEdge[] = [];

    for (const entity of entities) {
      for (const rel of entity.relationships ?? []) {
        const sourceId = keyToId.get(rel.sourceEntityKey);
        const targetId = keyToId.get(rel.targetEntityKey);
        if (!sourceId || !targetId) {
          continue;
        }

        const edgeKey = `${sourceId}|${targetId}|${rel.type}`;
        if (seen.has(edgeKey)) {
          continue;
        }
        seen.add(edgeKey);

        edges.push({
          id: edgeKey,
          sourceId,
          targetId,
          type: rel.type,
          label: this.shortRelationshipLabel(rel.type)
        });
      }
    }

    return edges;
  }

  private buildInitialLayout(entities: DynamicMetadataEntityCatalog[]): Record<string, NodePosition> {
    const count = entities.length;
    const columns = Math.max(1, Math.ceil(Math.sqrt(count)));
    const layout: Record<string, NodePosition> = {};

    entities.forEach((entity, index) => {
      const col = index % columns;
      const row = Math.floor(index / columns);
      const node = this.toNode(entity);
      layout[entity.id] = {
        x: NODE_GAP_X + col * (NODE_WIDTH + NODE_GAP_X),
        y: NODE_GAP_Y + row * (this.nodeHeight(node) + NODE_GAP_Y)
      };
    });

    return layout;
  }

  private shortRelationshipLabel(type: string): string {
    switch (type) {
      case 'OneToOne':
        return '1:1';
      case 'OneToMany':
        return '1:N';
      case 'ManyToMany':
        return 'M:N';
      default:
        return type;
    }
  }

  t(key: string): string {
    return this.localization.translate(key);
  }
}
